import React, { useContext, useMemo } from 'react';
import { Masonry } from 'antd';
import type { MasonryProps as AntMasonryProps } from 'antd/es/masonry/Masonry';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';
import { useResolvedDataSet } from '../../base/DataSetInheritContext';
import { DataSetInheritContext } from '../../base/DataSetInheritContext';

/**
 * MasonryItem 类型
 */
export interface AndMasonryItem {
  key: React.Key;
  column?: number;
  height?: number;
  children?: React.ReactNode;
  data?: any;
}

/**
 * AndMasonry Props 接口
 */
export interface AndMasonryProps extends Omit<AntMasonryProps, 'items' | 'onLayoutChange'>, BaseComponentProps, DataSetConfig {
  /** 瀑布流项 (可以是 JSON 字符串或数组) */
  items?: AndMasonryItem[] | string;
  /** 列数 */
  columns?: number | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number; xxl?: number };
  /** 间距 */
  gutter?: number | [number, number];
  /** 是否持续监听子项尺寸变化 */
  fresh?: boolean;
  /** 列排序回调动作 */
  layoutChangeAction?: string;
  /** 自定义项渲染函数 */
  itemRender?: (item: AndMasonryItem & { index: number }) => React.ReactNode;
  /** 列排序回调 */
  onLayoutChange?: (sortInfo: { key: React.Key; column: number }[]) => void;
}

/** 解析 items 配置 */
const parseItems = (items: AndMasonryItem[] | string | undefined): AndMasonryItem[] => {
  if (!items) return [];
  if (typeof items === 'string') {
    try {
      const parsed = JSON.parse(items);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return items;
};

/** 解析 columns 配置 */
const parseColumns = (columns: any): number | Record<string, number> | undefined => {
  if (columns === undefined || columns === null || columns === '') return undefined;
  if (typeof columns === 'number') return columns;
  if (typeof columns === 'string') {
    // 尝试解析为数字
    const num = parseInt(columns, 10);
    if (!isNaN(num)) return num;
    // 尝试解析为 JSON 对象 (响应式配置)
    try {
      return JSON.parse(columns);
    } catch {
      return undefined;
    }
  }
  return columns;
};

/** 解析 gutter 配置 */
const parseGutter = (gutter: any): number | [number, number] | undefined => {
  if (gutter === undefined || gutter === null || gutter === '') return undefined;
  if (typeof gutter === 'number') return gutter;
  if (typeof gutter === 'string') {
    // 尝试解析为数字
    const num = parseInt(gutter, 10);
    if (!isNaN(num)) return num;
    // 尝试解析为 JSON 数组 [horizontal, vertical]
    try {
      const parsed = JSON.parse(gutter);
      if (Array.isArray(parsed) && parsed.length === 2) {
        return parsed as [number, number];
      }
      return parsed;
    } catch {
      return undefined;
    }
  }
  if (Array.isArray(gutter)) return gutter as [number, number];
  return gutter;
};

/**
 * AndMasonry 瀑布流组件
 * 封装 Ant Design Masonry，支持设计器拖拽配置
 */
export const AndMasonry: React.FC<AndMasonryProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  items,
  columns,
  gutter,
  fresh,
  layoutChangeAction,
  onLayoutChange,
  itemRender,
  ...restProps
}) => {
  const resolved = useResolvedDataSet(dataSet, dataSetField, sessionId);
  const effectiveDataSet = resolved.dataSet;
  const effectiveDataSetField = resolved.dataSetField;
  const effectiveSessionId = resolved.sessionId;
  const inheritCtx = useContext(DataSetInheritContext);

  const addCommand = useAndStore((state) => state.addCommand);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );

  // 仅订阅数组长度：单元格 content 变更时不重渲染整块 Masonry（子 AndInput 自行字段级订阅）
  const storeArrayLength = useAndStore((state) => {
    if (!dataSetKey || !effectiveDataSetField) return -1;
    const data = state.getDataSet(dataSetKey, effectiveDataSetField);
    return Array.isArray(data) ? data.length : -1;
  });

  // columns 与 AndButton 等一致：优先从 propsStore 读取（setProps 后可立即刷新）
  // 行唯一：即便页面里多个 table 行共用同一个 AndMasonry id="ivgqx"，也通过 inheritCtx.index 使 key 区分开。
  const derivedPropsComponentId = useMemo(() => {
    if (!effectiveSessionId || id == null || id === '') return null;
    if (inheritCtx && typeof inheritCtx.index === 'number') {
      return `${String(id)}_${inheritCtx.index}`;
    }
    return String(id);
  }, [effectiveSessionId, id, inheritCtx]);

  const storeProps = useAndStore((state) =>
    derivedPropsComponentId
      ? state.propsStore[`${effectiveSessionId}_props`]?.[derivedPropsComponentId] ?? null
      : null
  );

  // 解析 items：优先 storeData，否则用 props
  const parsedItems = useMemo(() => {
    if (dataSetKey && effectiveDataSetField) {
      const data = getDataSet(dataSetKey, effectiveDataSetField);
      if (Array.isArray(data)) return data;
    }
    return parseItems(items);
  }, [storeArrayLength, dataSetKey, effectiveDataSetField, getDataSet, items]);

  const mergedColumns =
    storeProps?.columns !== undefined && storeProps?.columns !== null
      ? storeProps.columns
      : columns;

  const parsedColumns = useMemo(() => parseColumns(mergedColumns), [mergedColumns]);

  // 解析 gutter
  const parsedGutter = useMemo(() => parseGutter(gutter), [gutter]);

  // 处理 onLayoutChange
  const handleLayoutChange = useMemo(() => {
    if (onLayoutChange) return onLayoutChange;
    if (layoutChangeAction && effectiveSessionId) {
      return (sortInfo: { key: React.Key; column: number }[]) => {
        addCommand(effectiveSessionId, {
          actionName: layoutChangeAction,
          arg: sortInfo,
          timestamp: Date.now(),
        });
      };
    }
    return undefined;
  }, [onLayoutChange, layoutChangeAction, effectiveSessionId, addCommand]);

  // 设计模式下展示占位
  const displayItems = useMemo(() => {
    if (parsedItems.length > 0) return parsedItems;
    if (develop) {
      // 设计模式下提供示例项
      return [
        { key: '1', height: 150, data: { title: '示例项 1' } },
        { key: '2', height: 200, data: { title: '示例项 2' } },
        { key: '3', height: 120, data: { title: '示例项 3' } },
        { key: '4', height: 180, data: { title: '示例项 4' } },
        { key: '5', height: 140, data: { title: '示例项 5' } },
        { key: '6', height: 160, data: { title: '示例项 6' } },
      ];
    }
    return [];
  }, [parsedItems, develop]);

  // 默认 itemRender (设计模式)
  const defaultItemRender = useMemo(() => {
    if (itemRender) return itemRender;
    if (develop) {
      return (item: AndMasonryItem & { index: number }) => (
        <div
          style={{
            height: item.height || 100,
            background: '#f0f5ff',
            border: '1px dashed #91caff',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#1677ff',
            fontSize: 14,
          }}
        >
          {item.data?.title || `Item ${item.key}`}
        </div>
      );
    }
    return undefined;
  }, [itemRender, develop]);

  /** 子项 inherit 用 props 相对段名（如 config.columns），勿写 resolved 读数路径 */
  const inheritFieldRoot =
    typeof dataSetField === "string" && dataSetField.trim() !== ""
      ? dataSetField.trim()
      : undefined;

  const wrappedItemRender = useMemo(() => {
    if (!defaultItemRender) return undefined;
    if (!effectiveDataSet || !effectiveDataSetField || !inheritFieldRoot)
      return defaultItemRender;

    return (itemInfo: any, idx?: number) => {
      const resolvedIndex =
        typeof idx === 'number'
          ? idx
          : typeof itemInfo?.index === 'number'
            ? itemInfo.index
            : Number(itemInfo?.index ?? 0);

      return (
        <DataSetInheritContext.Provider
          value={{
            dataSet: effectiveDataSet,
            index: resolvedIndex,
            parentIndex: inheritCtx?.index,
            parentContext: inheritCtx ?? undefined,
            sessionId: effectiveSessionId,
            dataSetFieldRoot: inheritFieldRoot,
          }}
        >
          {defaultItemRender(itemInfo)}
        </DataSetInheritContext.Provider>
      );
    };
  }, [
    defaultItemRender,
    effectiveDataSet,
    effectiveDataSetField,
    inheritFieldRoot,
    effectiveSessionId,
    inheritCtx,
  ]);

  // 排除自定义 props，避免传入 DOM
  const {
    layoutChangeAction: _layoutChangeAction,
    ...otherProps
  } = restProps as any;

  return (
    <Masonry
      {...otherProps}
      items={displayItems as any}
      columns={parsedColumns as any}
      gutter={parsedGutter as any}
      fresh={fresh}
      itemRender={wrappedItemRender as any}
      onLayoutChange={handleLayoutChange}
    />
  );
};

export default AndMasonry;
